import firebase from 'firebase'
import db from '../../firebase/init'
import {
  processSubscription,
  DEFAULT_PERMISSIONS
} from '@/utils/subscriptionTiers'

const profile = db.collection('profile')
const customers = db.collection('customers')

const state = {
  isLoggedIn: false,
  profile: null,
  refererUrl: null,
  authTokenRefererUrl: null,
  permissions: { ...DEFAULT_PERMISSIONS }
}

const stripLegacyByokKey = profileData => {
  const safeProfile = { ...(profileData || {}) }
  const settings = safeProfile.settings
  const byok = settings?.byok

  if (byok?.openRouterApiKey) {
    const safeByok = { ...byok }
    delete safeByok.openRouterApiKey
    safeProfile.settings = {
      ...settings,
      byok: safeByok
    }
  }

  return safeProfile
}

const getters = {
  profile: state => state.profile,
  isLoggedIn: state => state.isLoggedIn,
  refererUrl: state => state.refererUrl,
  authTokenRefererUrl: state => state.authTokenRefererUrl,
  permissions: state => state.permissions
}

const actions = {
  setRefererUrl({ commit }, url) {
    commit('setRefererUrl', url)
  },
  setAuthTokenRefererUrl({ commit }, url) {
    commit('setAuthTokenRefererUrl', url)
  },
  setPermissions({ commit }, permissions) {
    commit('setPermissions', permissions)
  },
  getProfile({ commit }) {
    firebase.auth().currentUser?.uid
      ? profile
          .doc(firebase.auth().currentUser.uid)
          .get()
          .then(snapshot => {
            commit('setProfile', stripLegacyByokKey(snapshot.data()))
          })
      : null
  },

  getUserProfile({ commit: _ }, uid) {
    return firebase.auth().currentUser?.uid && uid
      ? profile
          .doc(uid)
          .get()
          .then(snapshot => {
            return stripLegacyByokKey(snapshot.data())
          })
      : null
  },

  finalizeLogin: async ({ commit }) => {
    // Wait for auth state to be ready (auth listener in main.js handles the actual state updates)
    return new Promise((resolve, reject) => {
      const unsubscribe = firebase.auth().onAuthStateChanged(
        async user => {
          unsubscribe() // Unsubscribe immediately - we only need one check
          if (user) {
            // Ensure state is updated (main.js listener should handle this, but just in case)
            commit('setIsLoggedIn', true)
            resolve(user)
          } else {
            reject(new Error('User not authenticated'))
          }
        },
        error => {
          unsubscribe()
          reject(error)
        }
      )
    })
  },

  logout: async ({ dispatch, commit }) => {
    sessionStorage.clear()

    // Clear profile state
    commit('clearProfile')

    // Reset permissions to defaults
    commit('setPermissions', { ...DEFAULT_PERMISSIONS })

    // Clean up all Firebase listeners before signing out
    dispatch('cleanupListeners', null, { root: true })

    await firebase.auth().signOut()

    // The home page is served by Nuxt, so leave the Vue 2 SPA entirely.
    if (typeof window !== 'undefined') {
      window.location.href = '/'
    }
  },

  getSubscription: () => {
    return new Promise((resolve, reject) => {
      if (!firebase.auth().currentUser) {
        resolve(false) // Resolve with false if user is not logged in
        return
      }

      const userId = firebase.auth().currentUser.uid
      let hasResolved = false // Flag to ensure Promise only resolves once

      customers
        .doc(userId)
        .collection('subscriptions')
        .onSnapshot(
          querySnapshot => {
            const allowedStatuses = ['trialing', 'active']
            let foundSubscription = false

            querySnapshot.forEach(doc => {
              const value = doc.data()
              if (allowedStatuses.includes(value.status)) {
                foundSubscription = value
              }
            })

            // Resolve only once (on first snapshot)
            if (!hasResolved) {
              hasResolved = true
              if (foundSubscription) {
                resolve(foundSubscription) // Resolve with the first matching subscription
              } else {
                resolve(false) // Resolve with false if no matching status found
              }
            }
          },
          error => {
            if (!hasResolved) {
              hasResolved = true
              reject(error) // Reject in case of an error
            }
          }
        )
    })
  },

  // Set up subscription listener - automatically updates permissions when subscription changes
  setupSubscriptionListener: ({ dispatch }) => {
    return new Promise((resolve, reject) => {
      const user = firebase.auth().currentUser
      if (!user) {
        resolve(null)
        return
      }

      let hasResolved = false

      const unsubscribe = customers
        .doc(user.uid)
        .collection('subscriptions')
        .onSnapshot(
          async querySnapshot => {
            try {
              // Find active or trialing subscription
              let activeSubscription = null
              querySnapshot.forEach(doc => {
                const sub = doc.data()
                if (['active', 'trialing'].includes(sub.status)) {
                  activeSubscription = sub
                }
              })

              // Get permissions from subscription (Firestore is source of truth)
              const permissions = processSubscription(activeSubscription)

              // Add admin/advertiser from token claims
              const idTokenResult = await user.getIdTokenResult()
              permissions.admin = idTokenResult.claims.admin || false
              permissions.advertiser = idTokenResult.claims.advertiser || false

              // Update store
              dispatch('setPermissions', permissions)

              // Resolve promise on first snapshot
              if (!hasResolved) {
                hasResolved = true
                resolve(unsubscribe)
              }
            } catch (error) {
              if (!hasResolved) {
                hasResolved = true
                reject(error)
              }
            }
          },
          error => {
            if (!hasResolved) {
              hasResolved = true
              reject(error)
            }
          }
        )
    })
  }
}

const mutations = {
  setProfile(state, profile) {
    state.profile = profile
  },
  clearProfile(state) {
    state.profile = null
  },
  setIsLoggedIn: (state, value) => {
    state.isLoggedIn = value
  },
  setRefererUrl: (state, value) => {
    state.refererUrl = value
  },
  setAuthTokenRefererUrl: (state, value) => {
    state.authTokenRefererUrl = value
  },
  setPermissions: (state, permissions) => {
    // Ensure we always have a complete permissions object
    state.permissions = { ...DEFAULT_PERMISSIONS, ...permissions }
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
